// "Carica altre collaborazioni" + ricerca live + filtro per tag per la
// griglia dell'archivio Collaborazioni & Brand (archive-progetti.php),
// sostenute dall'endpoint REST castro/v1/collaborazioni
// (inc/collaborazioni-rest-controller.php). Stesso principio di
// assets/js/archivio-servizi.js (che vedi per i commenti estesi sul perché il
// markup PHP renderizza server-side solo la prima pagina, con la
// paginazione standard di WordPress come fallback senza JS): qui in più i
// chip di filtro per tag, che si combinano con la ricerca testuale (non la
// sostituiscono) e riavviano la stessa richiesta da zero, come una ricerca.
import { animateCardsIn, animateCardsOut } from "./archivio-card-animations";

const SEARCH_DEBOUNCE_MS = 300;

function debounce(fn, delay) {
    let timer = null;

    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    };
}

function initArchivioCollaborazioniSection(section) {
    const grid = section.querySelector("[data-collab-grid]");

    if (!grid) {
        return;
    }

    const searchInput = section.querySelector("[data-collab-search-input]");
    const emptyMessage = section.querySelector("[data-collab-search-empty]");
    const countLabel = section.querySelector("[data-collab-count]");
    const loadMoreWrap = section.querySelector("[data-collab-load-more-wrap]");
    const loadMoreButton = section.querySelector("[data-collab-load-more]");
    const showLessButton = section.querySelector("[data-collab-show-less]");
    const shownCountLabel = section.querySelector("[data-collab-shown-count]");
    const loadMoreError = section.querySelector("[data-collab-load-more-error]");
    const pagination = section.querySelector(".archivio-collaborazioni__pagination");
    const filterButtons = Array.from(section.querySelectorAll("[data-collab-filter]"));

    const settings = (window.Castro && window.Castro.settings) || {};
    const apiUrl = `${settings.api_url || ""}castro/v1/collaborazioni`;

    // Quanto è già renderizzato server-side (vedi $collab_shown in
    // archive-progetti.php): è anche la dimensione a cui torna "Mostra meno".
    const baseCount = parseInt(loadMoreWrap ? loadMoreWrap.dataset.collabShown : "0", 10) || 0;

    let shown = baseCount;
    let total = parseInt(section.dataset.collabTotal, 10) || 0;
    let currentSearch = "";
    let currentTag = "";
    // Scarta le risposte di richieste superate (l'utente ha digitato/cliccato
    // altro nel frattempo): solo l'ultima richiesta partita può aggiornare il DOM.
    let requestId = 0;

    // Con JS attivo la paginazione classica (unico modo di sfogliare l'elenco
    // senza JS) lascia il posto a "Carica altre collaborazioni"/ricerca/filtro.
    if (pagination) {
        pagination.hidden = true;
    }

    function updateCount(value) {
        if (!countLabel) {
            return;
        }

        const label = value === 1 ? "collaborazione" : "collaborazioni";
        countLabel.textContent = `${value} ${label}`;
    }

    function updateLoadMoreUi(hasMore) {
        if (loadMoreWrap) {
            loadMoreWrap.hidden = total <= baseCount;
        }

        if (loadMoreButton) {
            loadMoreButton.hidden = !hasMore;
        }

        if (showLessButton) {
            showLessButton.hidden = shown <= baseCount;
        }

        if (shownCountLabel) {
            shownCountLabel.textContent = total ? `Mostrate ${shown} di ${total} collaborazioni` : "";
        }
    }

    async function fetchCollaborazioni({ search, tag, offset, perPage }) {
        const params = new URLSearchParams({
            search,
            tag,
            offset: String(offset),
            per_page: String(perPage),
        });

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
            headers: { Accept: "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Richiesta fallita (${response.status})`);
        }

        return response.json();
    }

    function runQuery() {
        const thisRequestId = ++requestId;
        const perPage = baseCount;

        fetchCollaborazioni({ search: currentSearch, tag: currentTag, offset: 0, perPage })
            .then((data) => {
                if (thisRequestId !== requestId) {
                    return;
                }

                grid.innerHTML = data.html;
                shown = data.shown;
                total = data.total;

                if (emptyMessage) {
                    emptyMessage.hidden = total > 0;
                }

                updateCount(total);
                updateLoadMoreUi(data.hasMore);
                animateCardsIn(Array.from(grid.children));
            })
            .catch((error) => {
                // Ricerca/filtro non critici: se la richiesta fallisce si
                // lascia la griglia com'era, senza rompere il resto della pagina.
                console.error("[archivio-collaborazioni]", error);
            });
    }

    const debouncedSearch = debounce(runQuery, SEARCH_DEBOUNCE_MS);

    if (searchInput) {
        searchInput.addEventListener("input", () => {
            currentSearch = searchInput.value.trim();
            debouncedSearch();
        });
    }

    filterButtons.forEach((button) => {
        button.addEventListener("click", () => {
            if (button.classList.contains("is-active")) {
                return;
            }

            currentTag = button.dataset.collabFilterValue || "";
            filterButtons.forEach((btn) => btn.classList.toggle("is-active", btn === button));
            runQuery();
        });
    });

    if (loadMoreButton) {
        loadMoreButton.addEventListener("click", () => {
            const thisRequestId = ++requestId;

            if (loadMoreError) {
                loadMoreError.hidden = true;
            }
            loadMoreButton.classList.add("is-loading");

            fetchCollaborazioni({ search: currentSearch, tag: currentTag, offset: shown, perPage: baseCount })
                .then((data) => {
                    if (thisRequestId !== requestId) {
                        return;
                    }

                    const previousCardCount = grid.children.length;

                    // data.shown è già "offset + card restituite in questa
                    // risposta" (calcolato server-side): sostituisce shown
                    // per intero, non si somma a quello precedente.
                    grid.insertAdjacentHTML("beforeend", data.html);
                    shown = data.shown;
                    total = data.total;
                    updateLoadMoreUi(data.hasMore);
                    animateCardsIn(Array.from(grid.children).slice(previousCardCount));
                })
                .catch((error) => {
                    // Prima c'era solo un console.error: la richiesta poteva
                    // fallire (rete/server) senza nessun riscontro a schermo,
                    // facendo sembrare il bottone "rotto" a chi non ha la
                    // console aperta.
                    console.error("[archivio-collaborazioni]", error);
                    if (loadMoreError) {
                        loadMoreError.hidden = false;
                    }
                })
                .finally(() => {
                    if (thisRequestId === requestId) {
                        loadMoreButton.classList.remove("is-loading");
                    }
                });
        });
    }

    if (showLessButton) {
        showLessButton.addEventListener("click", () => {
            const cards = Array.from(grid.querySelectorAll(".archivio-collaborazioni__card")).slice(baseCount);

            showLessButton.classList.add("is-loading");
            animateCardsOut(cards, () => {
                showLessButton.classList.remove("is-loading");
                shown = baseCount;
                updateLoadMoreUi(total > baseCount);
            });
        });
    }

    updateLoadMoreUi(total > shown);
}

export function initArchivioCollaborazioni() {
    document.querySelectorAll("[data-archivio-collaborazioni]").forEach(initArchivioCollaborazioniSection);
}
